/**
 * @fileoverview Component for displaying a table of sites.
 * This component includes a header with columns and a body with site data.
 */

import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { DateTime } from 'luxon'
import SiteBigIcon from '../../assets/site-big.svg'
import { formatOutput } from '../../utils/formatOutput'

/**
 * @function formatDate
 * @description Formats a timestamp to a relative date.
 * @param {string} timestamp - The timestamp to format.
 * @returns {JSX.Element} - A React component that renders the formatted date.
 */
function formatDate(timestamp: string): JSX.Element {
    const dt = DateTime.fromISO(timestamp)
    return <span>{dt.toRelative()}</span>
}

const columns = ['Site', 'Users', 'Studies', 'Last active']

/**
 * @interface SiteListTableProps
 * @description Props for the SiteListTable component.
 * @property {Novoic.API.Site[]} data - The data to display in the table.
 * @property {(e: unknown, row: string) => void} onRowClicked - The function to call when a row is clicked.
 */
interface SiteListTableProps {
    data: Novoic.API.Site[]
    onRowClicked: (e: unknown, row: string) => void
}

export function SiteListTable({ data, onRowClicked }: SiteListTableProps) {
    return (
        <table className="w-full">
            <thead className="nv-thead top-[161px] sticky">
                <tr className="h-12">
                    {columns.map((col, index) => (
                        <th
                            key={col}
                            scope="col"
                            className={`${
                                index === 0 ? 'pl-8 pr-2.5' : 'px-2.5'
                            } text-left text-sm font-medium text-gray-900 whitespace-nowrap`}
                        >
                            <button className="group inline-flex">
                                {col}
                                <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                            </button>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
                {data.map((site: Novoic.API.Site) => (
                    <tr
                        key={site.id}
                        className="hover:bg-[#f7f9ff] cursor-pointer transition-all"
                        onClick={(e) => onRowClicked(e, site.id)}
                    >
                        <td
                            className="pl-8 flex items-center gap-x-2 whitespace-nowrap pr-10 py-4 text-sm"
                            role="presentation"
                        >
                            <div className="flex gap-x-3 items-center">
                                <SiteBigIcon className="fill-indigo-500 w-8 h-8 stroke-indigo-50" />
                                <div>
                                    <p className="font-medium">{formatOutput(site.name)}</p>
                                    <p className="text-gray-600 text-xs font-normal">
                                        {formatOutput(site.address)}
                                    </p>
                                </div>
                            </div>
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm font-medium text-gray-600">
                            <p>{formatOutput(site.users_count)}</p>
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm font-medium text-gray-600">
                            <p>{formatOutput(site.studies_count)}</p>
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                            {formatDate(site.last_activity_at)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
